import type { DayAnalytics } from '@/services/timeEntries/types';

/**
 * Count worked days eligible for service charge distribution,
 * optionally excluding days where total hours are not strictly above the threshold.
 *
 * @param dayAnalytics - Per-day breakdown from time entry analytics
 * @param totalWorkedDays - Pre-computed total worked days (fallback if dayAnalytics unavailable)
 * @param excludeBelowHours - Threshold: exclude days with totalHours <= this value. null/0 = no filter.
 * @returns Number of eligible worked days
 *
 * SYNC: An identical implementation exists in
 *       supabase/functions/_shared/serviceCharge.ts — keep both in sync.
 */
export function countEligibleServiceChargeDays(
  dayAnalytics: DayAnalytics[] | undefined,
  totalWorkedDays: number,
  excludeBelowHours: number | null
): number {
  // If no threshold configured, return all worked days
  if (excludeBelowHours === null || excludeBelowHours <= 0) {
    return totalWorkedDays;
  }

  // If dayAnalytics is unavailable, fall back to unfiltered count
  if (!dayAnalytics || dayAnalytics.length === 0) {
    return totalWorkedDays;
  }

  // Count days that have shifts AND hours strictly above the threshold
  return dayAnalytics.filter(
    day => day.shiftCount > 0 && day.totalHours > excludeBelowHours
  ).length;
}
